import { useContext, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { DashboardDateSelectionContext } from "./DashboardDateSelectionContext";
import {
  dateSelectionHash,
  readDateSelection,
  resolveDates,
  type DateRangePreset,
} from "./dateSelection";
import { getBrowserHash, updateBrowserHash } from "../../utils/urlHash";
import { hasLocalActivityScope } from "./activityScope";

/** URL dates win on navigation; otherwise all dashboard pages inherit the saved selection. */
export function useDashboardDates() {
  const location = useLocation();
  const localScope = hasLocalActivityScope(location);
  const { selection, remember } = useContext(DashboardDateSelectionContext);
  const [state, setState] = useState(() => ({
    key: location.key,
    dates: readDateSelection(getBrowserHash(), selection),
  }));
  // Restore before rendering consumers, so a new route never fetches with the old scope.
  let dates = state.dates;
  if (state.key !== location.key) {
    dates = readDateSelection(getBrowserHash(), selection);
    setState({ key: location.key, dates });
  }
  useEffect(() => {
    if (state.key !== location.key) return;
    updateBrowserHash(dateSelectionHash(state.dates));
    if (!localScope) remember(state.dates);
  }, [state, location.key, localScope, remember]);

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout>;
    const refresh = () => {
      setState(current => {
        const next = resolveDates(current.dates);
        return next.from === current.dates.from && next.to === current.dates.to
          ? current
          : { ...current, dates: next };
      });
      clearTimeout(timer);
      const midnight = new Date();
      midnight.setHours(24, 0, 0, 0);
      timer = setTimeout(refresh, midnight.getTime() - Date.now() + 100);
    };
    refresh();
    window.addEventListener("focus", refresh);
    return () => {
      clearTimeout(timer);
      window.removeEventListener("focus", refresh);
    };
  }, []);

  return {
    ...dates,
    // Named for DashboardDateRange, so pages can spread these straight into it.
    rememberedCustomRange: dates.customRange,
    onChange: (from: string, to: string, preset: DateRangePreset = from || to ? "custom" : "all") =>
      setState(current => ({
        ...current,
        dates: resolveDates({
          from,
          to,
          preset,
          customRange: preset === "custom" ? { from, to } : current.dates.customRange,
        }),
      })),
  };
}
